import { useState, useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Activity,
  BookOpen,
  TrendingUp,
  ArrowLeftRight,
  DollarSign,
  Tags,
  Link2,
  Settings,
  User,
  LogOut,
  Bell,
  Coins,
  BarChart3,
  LineChart,
  Shield,
  EyeOff,
  PanelTopOpen,
  SlidersHorizontal,
  Sun,
  Moon,
  Database,
  Zap,
  GitCompare,
  Clock3,
  ChevronDown,
  Briefcase,
  ClipboardCheck,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { FEATURE_SCREENER_ENABLED } from '@/lib/featureFlags'
import { Button } from '@/components/ui/button'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import { PositionManager } from '@/components/positions'
import { NotificationManager } from '@/components/notifications/NotificationManager'
import { MarketHoursWidget } from '@/components/common/MarketHoursWidget'

const HIDDEN_TABS_KEY = 'cparser-hidden-tabs'

interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  permission?: string
  adminOnly?: boolean
  external?: boolean
}

interface NavSectionDef {
  title: string
  items: NavItem[]
}

const navSections: NavSectionDef[] = [
  {
    title: 'Overview',
    items: [
      ...(FEATURE_SCREENER_ENABLED ? [
        { title: 'Screener', href: '/screener', icon: LineChart },
        { title: 'Orderbooks screener', href: '/orderbooks-screener', icon: BookOpen },
      ] : []),
      { title: 'Dashboard', href: '/', icon: Bell },
      { title: 'Positions', href: '/positions', icon: Briefcase },
      { title: 'Coin', href: '/coin-overview', icon: Coins },
      { title: 'Orderbook', href: '/orderbook-overview', icon: BarChart3 },
    ],
  },
  {
    title: 'Alerts',
    items: [
      { title: 'Review alerts', href: '/alert-reviews', icon: ClipboardCheck },
      { title: 'OI', href: '/alerts', icon: Activity },
      { title: 'Orderbook', href: '/orderbook', icon: BookOpen },
      { title: 'Mark Price', href: '/mark-price', icon: TrendingUp },
      { title: 'Funding', href: '/funding', icon: DollarSign },
      { title: 'Price Diff', href: '/price-diff', icon: ArrowLeftRight },
      { title: 'Splash', href: '/splash', icon: Zap },
      { title: 'Spread', href: '/spread', icon: GitCompare },
    ],
  },
  {
    title: 'Data Management',
    items: [
      { title: 'Tags', href: '/tags', icon: Tags },
      { title: 'Aliases', href: '/aliases', icon: Link2 },
    ],
  },
  {
    title: 'Settings',
    items: [
      { title: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
      { title: 'Account', href: '/account', icon: User },
      { title: 'System Debug', href: '/admin-debug', icon: Shield, adminOnly: true },
      { title: 'DB Browser', href: '/db-browser', icon: Database, adminOnly: true },
    ],
  },
]

function loadHiddenTabs(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_TABS_KEY)
    if (raw) return new Set(JSON.parse(raw))
  } catch { /* ignore */ }
  return new Set()
}

function saveHiddenTabs(set: Set<string>) {
  localStorage.setItem(HIDDEN_TABS_KEY, JSON.stringify([...set]))
}

interface SidebarProps {
  hidden: boolean
  onToggleHidden: () => void
}

export function Sidebar({ hidden, onToggleHidden }: SidebarProps) {
  const location = useLocation()
  const { user, isAdmin, hasPermission, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [hiddenTabs, setHiddenTabs] = useState<Set<string>>(loadHiddenTabs)

  const toggleTab = useCallback((href: string) => {
    setHiddenTabs(prev => {
      const next = new Set(prev)
      if (next.has(href)) next.delete(href)
      else next.add(href)
      saveHiddenTabs(next)
      return next
    })
  }, [])

  const toggleSection = useCallback((items: NavItem[], allHidden: boolean) => {
    setHiddenTabs(prev => {
      const next = new Set(prev)
      for (const item of items) {
        if (allHidden) next.delete(item.href)
        else next.add(item.href)
      }
      saveHiddenTabs(next)
      return next
    })
  }, [])

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const canAccess = (item: NavItem) => {
    if (item.adminOnly && !isAdmin) return false
    if (item.permission && !hasPermission(item.permission)) return false
    return true
  }

  // Compute visible sections (filter by access + hidden)
  const visibleSections = useMemo(() => {
    return navSections
      .map(section => ({
        ...section,
        items: section.items.filter(item => canAccess(item) && !hiddenTabs.has(item.href)),
      }))
      .filter(section => section.items.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenTabs, isAdmin])

  const NavLink = ({ item }: { item: NavItem }) => {
    const baseClass = cn(
      'inline-flex h-8 shrink-0 items-center gap-2 rounded-md px-3 text-sm transition-colors',
      isActive(item.href)
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
    )

    if (item.external) {
      return (
        <a href={item.href} className={baseClass}>
          <item.icon className="h-4 w-4 shrink-0" />
          <span>{item.title}</span>
        </a>
      )
    }

    return (
      <Link to={item.href} className={baseClass}>
        <item.icon className="h-4 w-4 shrink-0" />
        <span>{item.title}</span>
      </Link>
    )
  }

  const NavSection = ({ section }: { section: NavSectionDef }) => (
    <div className="flex items-center gap-1">
      <span className="px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {section.title}
      </span>
      {section.items.map((item) => (
        <NavLink key={item.href} item={item} />
      ))}
    </div>
  )

  if (hidden) {
    return (
      <Button
        variant="outline"
        size="icon"
        onClick={onToggleHidden}
        className="fixed left-3 top-3 z-40 shadow-sm"
      >
        <PanelTopOpen className="h-4 w-4" />
      </Button>
    )
  }

  return (
    <div className="border-b bg-sidebar">
      <div className="flex h-14 items-center gap-4 px-4">
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <Activity className="h-6 w-6 text-primary" />
          <span>Coin Parser</span>
        </Link>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-2 border-r pr-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2" title="Market Hours">
                  <Clock3 className="h-4 w-4" />
                  <span className="hidden xl:inline">Market Hours</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-[calc(100vw-2rem)] max-w-none p-4"
              >
                <MarketHoursWidget />
              </PopoverContent>
            </Popover>

            <PositionManager />
            <NotificationManager />
          </div>

          {user && (
            <div className="hidden items-center gap-2 rounded-md px-2 py-1 md:flex">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <div className="max-w-28 overflow-hidden">
                <p className="truncate text-sm font-medium leading-tight">{user.username}</p>
                <p className="truncate text-xs text-muted-foreground capitalize leading-tight">
                  {user.role}
                </p>
              </div>
            </div>
          )}

          <Button variant="ghost" size="sm" onClick={() => logout()}>
            <LogOut className="h-4 w-4" />
            <span className="ml-2">Logout</span>
          </Button>

          {/* Theme toggle */}
          <Button variant="ghost" size="icon" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {/* Customize visible tabs */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" title="Customize visible tabs">
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-0">
              <div className="px-3 py-2 border-b">
                <p className="text-sm font-medium">Visible Tabs</p>
                <p className="text-xs text-muted-foreground">Toggle tabs to show or hide them</p>
              </div>
              <div className="max-h-72 overflow-y-auto py-1">
                {navSections.map((section) => {
                  const accessibleItems = section.items.filter(canAccess)
                  if (accessibleItems.length === 0) return null
                  const allHidden = accessibleItems.every(i => hiddenTabs.has(i.href))
                  return (
                    <div key={section.title}>
                      <button
                        className="flex w-full items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-accent/50 transition-colors"
                        onClick={() => toggleSection(accessibleItems, allHidden)}
                        title={allHidden ? 'Show all' : 'Hide all'}
                      >
                        {section.title}
                        <span className="text-[10px] normal-case tracking-normal font-normal">
                          {allHidden ? 'show all' : 'hide all'}
                        </span>
                      </button>
                      {accessibleItems.map((item) => (
                        <label
                          key={item.href}
                          className="flex items-center gap-3 px-3 py-1.5 cursor-pointer hover:bg-accent/50 transition-colors"
                        >
                          <item.icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="flex-1 text-sm">{item.title}</span>
                          <Switch
                            checked={!hiddenTabs.has(item.href)}
                            onCheckedChange={() => toggleTab(item.href)}
                          />
                        </label>
                      ))}
                    </div>
                  )
                })}
              </div>
            </PopoverContent>
          </Popover>

          <Button variant="ghost" size="icon" onClick={onToggleHidden}>
            <EyeOff className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="border-t px-2 py-2">
        <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1">
          {visibleSections.map((section, idx) => (
            <div key={section.title} className="contents">
              {idx > 0 && <div className="h-5 w-px shrink-0 bg-border" />}
              <NavSection section={section} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
